/**
 * Runtime mount-point (basename) resolver for the KDS SPA.
 *
 * WS-PHASE2-SHELL Stage 1. The KDS operator SPA must be mount-agnostic:
 * it has to keep working at its CURRENT admin-shell mount
 * (`/m/{merchant_id}/kds`) AND also render correctly when the Phase-2
 * merchant shell serves it under a fixed `/kds` prefix
 * (`app.tovrik.com/kds`).
 *
 * This SPA does NOT use React Router — routing is a hand-rolled
 * `parseRoute()` in App.tsx that matches the absolute pathname. The
 * "basename" here is the URL prefix the app is mounted under; we strip
 * it off the pathname before route parsing so the same route table
 * works regardless of mount.
 *
 * Design constraints (per task brief):
 *   • EXPLICIT allowlist, not a generic prefix detector. Only known,
 *     intentional mount points are recognised. Anything else falls
 *     through to the legacy behaviour (empty basename) so standalone
 *     dev (`/`, `/pair`, `/releases`) and the existing merchant mount
 *     keep working unchanged.
 *   • The existing `/m/{mid}/kds` STAFF mount is dynamic (merchant id in
 *     the path) and is handled inside parseRoute's regex — we do NOT treat
 *     it as a strippable basename, we leave that path shape to parseRoute.
 *     `resolveBasename` therefore returns `''` for the staff mount (see the
 *     WHY comment below): the ONLY fixed-prefix mount it recognises is the
 *     STATIC `/kds` shell mount that parseRoute does not understand.
 *
 * SINGLE SOURCE OF TRUTH (2026-07-09, staff-support-mount): the mount table
 * is no longer re-declared here — the STATIC `/kds` prefix and its
 * segment-aware match come from `@tovriktech/shell-mounts` (`matchMount` +
 * `STATIC_MOUNT`), the one canonical matcher admin-router and every SPA share.
 */

import { matchMount, STATIC_MOUNT } from '@tovriktech/shell-mounts';

/**
 * The fixed STATIC mount prefix the SPA may be served under, DERIVED from the
 * canonical `STATIC_MOUNT.kds` (`/kds`) so it can never drift from the shell
 * contract. Matched segment-aware: `/kds`, `/kds/`, `/kds/pair` → `/kds`;
 * `/kdss` does NOT.
 *
 * The dynamic `/m/{mid}/kds` STAFF mount is intentionally absent — it's
 * resolved by parseRoute's own regex, not by basename stripping.
 */
export const KNOWN_MOUNT_PREFIXES = [STATIC_MOUNT.kds] as const;

export type KnownMountPrefix = (typeof KNOWN_MOUNT_PREFIXES)[number];

/**
 * Resolve the basename (mount prefix) for a given pathname.
 *
 * Returns the STATIC `/kds` shell prefix when the pathname is served under
 * the one-origin Phase-2 mount, or `''` (no basename) for the STAFF mount
 * (`/m/{mid}/kds`) and standalone dev — in which case parseRoute handles the
 * full pathname as before.
 *
 * WHY the STAFF mount returns `''` (do NOT change): App.tsx keys its
 * `isShell` / session-merchant shell flow on `resolveBasename(path) !== ''`.
 * Under the staff mount the URL carries the merchant being acted ON
 * (`/m/{mid}/kds`), and parseRoute must use THAT merchant — NOT the staffer's
 * own session merchant. Returning a basename here would flip App into the
 * shell flow and resolve the wrong (staffer's) merchant via
 * `/api/v1/me/merchants`. Same-origin API routing for the staff mount is
 * handled separately in `resolveKdsApiBase` (which DOES honour the staff
 * scheme); the basename resolver deliberately does not.
 *
 * The default path delegates to the canonical `matchMount` (STATIC + KDS
 * only). A caller-supplied `allowlist` (test/flex affordance — never used in
 * app code) falls back to a generic segment-aware prefix match against that
 * verbatim list.
 */
export function resolveBasename(
  pathname: string,
  allowlist: readonly string[] = KNOWN_MOUNT_PREFIXES,
): string {
  if (allowlist === KNOWN_MOUNT_PREFIXES) {
    // Canonical path: the KDS SPA is only mounted at its STATIC `/kds` prefix.
    // A STATIC + product==='kds' match → the basename; the STAFF scheme and
    // every other pathname → '' (see WHY above).
    const m = matchMount(pathname);
    return m && m.scheme === 'static' && m.product === 'kds' ? m.basename : '';
  }
  for (const prefix of allowlist) {
    if (pathname === prefix) return prefix;
    if (pathname.startsWith(prefix + '/')) return prefix;
  }
  return '';
}

/**
 * Strip a resolved basename off a pathname, returning the in-app path.
 *
 * `stripBasename('/kds/pair', '/kds')` → `'/pair'`
 * `stripBasename('/kds', '/kds')`      → `'/'`
 * `stripBasename('/m/mer_x/kds', '')`          → `'/m/mer_x/kds'` (unchanged)
 *
 * The result always starts with `/` so it can be fed straight into
 * parseRoute, which expects an absolute in-app path.
 */
export function stripBasename(pathname: string, basename: string): string {
  if (!basename) return pathname;
  if (pathname === basename) return '/';
  if (pathname.startsWith(basename + '/')) {
    const rest = pathname.slice(basename.length);
    return rest.startsWith('/') ? rest : '/' + rest;
  }
  return pathname;
}
